import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { NavigateOptions } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useModal } from '@context/ModalsContext'
import { useArabicSessionLanguage } from '@context/SessionSettingsContext'
import { useSnackbar } from '@context/SnackbarContext'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Body1, Body2, Heading6 } from '@ui/Typography'
import { Button } from '@ui/components/Button'

import { useGAEvents } from '@utils/callGAEvents'
import { CommonPaths } from '@utils/path'
import {
  getIBTransactionFieldsByTransactionType,
  getTransactionAmountSign,
} from '@utils/transaction.utils'
import { WalletRestrictions, isRestricted } from '@utils/wallet.utils'

import { HorizontalScroll } from '@global/HorizontalScroll/HorizontalScroll'

import { useFormatNumber } from '@hooks/useFormatNumber'
import { useWithdrawalPageNavigation } from '@hooks/useWithdrawalPageNavigation'

import { Loading } from '@shared/components/Loading/Loading'
import { TransactionBadgeMap } from '@shared/components/StatusBadge/StatusBadge'
import { SupportModal } from '@shared/modals/Support.modal'

import { CurrencyCodeFlagIcon } from '../components/CurrencyCodeFlagIcon/CurrencyCodeFlagIcon'
import { SectionHeader } from '../components/SectionHeader/SectionHeader'
import { WalletWithLatestTransactions } from '../hooks/useWalletsWithLatestTransactions'

import styles from './Wallets.module.scss'

interface IBWalletProps {
  walletsWithLatestTransactions: WalletWithLatestTransactions[]
  isLoading: boolean
}

const MAX_WALLETS = 2
const NAVIGATE_OPTIONS: NavigateOptions = { state: { from: 'dashboard' } }

export const Wallets: FC<IBWalletProps> = (props) => {
  const { walletsWithLatestTransactions, isLoading } = props
  const { account } = useAccountReadContext()
  const slicedWalletsWithLatestTransactions = walletsWithLatestTransactions.slice(0, MAX_WALLETS)

  const { t } = useTranslation()
  const navigate = useNavigate()
  const { formatMoney } = useFormatNumber()
  const isArabic = useArabicSessionLanguage()
  const { addSnackbar } = useSnackbar()
  const { callGAEvents } = useGAEvents()
  const restrictionsModal = useModal(SupportModal, { title: t('Temporarily unavailable') })
  const { handleWithdrawalNavigation, walletWithdrawLoadingId } = useWithdrawalPageNavigation()

  if (!slicedWalletsWithLatestTransactions.length && !isLoading) {
    return null
  }

  const handleWithdrawal = (isWithdrawRestricted: boolean, walletId: string): void => {
    if (isWithdrawRestricted) {
      addSnackbar.error({ message: t('This action is not available') })
      restrictionsModal.open()
      return
    }
    callGAEvents().IBDashboard.Wallet.withdraw()
    handleWithdrawalNavigation({
      walletId,
      navigateOptions: NAVIGATE_OPTIONS,
    })
  }

  return (
    <div className={styles.iBWallet}>
      <SectionHeader
        title={t('IB.Dashboard.IB Wallet')}
        seeMoreLinkProps={{
          onClick: () => callGAEvents().IBDashboard.Wallet.seeMore(),
          to: CommonPaths.IntroducingBrokerWallets,
        }}
      />

      <Loading isLoading={isLoading} showLoadingIcon>
        <HorizontalScroll
          containerClassName={styles.walletsContainer}
          innerClassName={styles.walletsContainerScroll}
        >
          {slicedWalletsWithLatestTransactions.map((walletWithLatestTransaction) => {
            const { wallet, latestTransactions } = walletWithLatestTransaction

            const latestTransaction = latestTransactions.length ? latestTransactions[0] : undefined

            const fields = latestTransaction
              ? getIBTransactionFieldsByTransactionType(
                  latestTransaction.type.id,
                  latestTransaction,
                  isArabic,
                  account?.id
                )
              : undefined

            const isWithdrawRestricted = isRestricted(
              WalletRestrictions.WITHDRAW,
              wallet.restrictions
            )

            const isTransferFromRestricted = isRestricted(
              WalletRestrictions.TRANSFER_FROM,
              wallet.restrictions
            )

            return (
              <div key={wallet.id} className={styles.wallet}>
                <div className={styles.header}>
                  <div className={styles.currency}>
                    <CurrencyCodeFlagIcon currencyType={wallet.currency.id} />

                    <Body1 medium>{wallet.currency.id}</Body1>
                  </div>

                  {latestTransaction && (
                    <SensitiveData>
                      <div className={styles.status}>
                        <Body2 className={styles.statusTitle}>
                          {t(TransactionBadgeMap[latestTransaction.state.id].label)}
                        </Body2>

                        {fields && (
                          <Body1 medium>
                            <span>
                              {getTransactionAmountSign(
                                latestTransaction.type.id,
                                false,
                                latestTransaction,
                                account?.id
                              )}
                            </span>
                            {formatMoney(fields.amount, fields.currency)}
                          </Body1>
                        )}
                      </div>
                    </SensitiveData>
                  )}
                </div>
                <div className={styles.info}>
                  <Heading6 medium>
                    <SensitiveData>{formatMoney(wallet.balance, wallet.currency.id)}</SensitiveData>
                  </Heading6>
                  <Body1 className={styles.walletName}>{wallet.name}</Body1>
                </div>
                <div className={styles.buttons}>
                  <div className={styles.button}>
                    <Button.Primary
                      size='lg'
                      fullWidth
                      disabled={isWithdrawRestricted}
                      onClick={() => handleWithdrawal(isWithdrawRestricted, wallet.id)}
                      loading={walletWithdrawLoadingId === wallet.id}
                    >
                      {t('Withdraw')}
                    </Button.Primary>
                  </div>

                  <div className={styles.button}>
                    <Button.Primary
                      size='lg'
                      fullWidth
                      disabled={isTransferFromRestricted}
                      onClick={() => {
                        if (isTransferFromRestricted) {
                          addSnackbar.error({ message: t('This action is not available') })
                          restrictionsModal.open()
                          return
                        }

                        callGAEvents().IBDashboard.Wallet.transfer()
                        navigate(`${CommonPaths.IntroducingBrokerWallets}/${wallet.id}/transfer`)
                      }}
                    >
                      {t('Transfer')}
                    </Button.Primary>
                  </div>
                </div>
              </div>
            )
          })}
        </HorizontalScroll>
      </Loading>
    </div>
  )
}
